import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    Box,
    Button,
    Flex,
    Image,
    Loader,
    Paper,
    Skeleton,
    Tabs,
    Text,
    Title,
    useMantineColorScheme,
} from "@mantine/core";
import { IconCheck, IconChevronLeft, IconEyeCheck } from "@tabler/icons-react";
import { CampaignState, ReviewState } from "src/graphql/generated/graphql";

import {
    AssignmentTab,
    CampaignApplicationsTab,
    MediaTab,
    OverviewWidget,
    ReportTab,
} from "../../components/CampaignDetail";
import { TimelineWidget } from "../../components/CampaignDetail/tabs/overview/widgets/timeline";
import { ReviewsTab } from "../../components/CampaignDetail/tabs/reviews";
import { useApproveCampaign, useCampaign, useCampaignQuestionnaire } from "../../hooks/campaigns";
import { useCampaignReviews } from "../../hooks/reviews";

const defaultTab = "assignment";

export interface CampaignDetailPageProps {}

export const CampaignDetailPage = ({}: CampaignDetailPageProps) => {
    const { t } = useTranslation(["common"]);
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const productId = searchParams.get("productId");
    const { colorScheme } = useMantineColorScheme();

    const tab = params?.tab || defaultTab;

    const { campaign, isLoadingCampaign } = useCampaign(params?.campaignId);
    const isDraft = campaign?.state === CampaignState.Draft;
    const tabDisabledText = isDraft ? t("campaign_tab_disabled_draft") : undefined;
    // The Report tab is driven entirely by the evaluated campaign.results — disable it until
    // the campaign has been evaluated (results computed by an admin).
    const isEvaluated = (campaign?.results?.products?.length || 0) > 0;
    // When the campaign isn't evaluated yet, say so in the tab label itself (not just a tooltip)
    // so it's clear why the Report tab is disabled.
    const reportTabLabel =
        !isDraft && !isEvaluated ? t("campaign_tab_report_not_evaluated") : t("campaign_tab_report");
    const { questionnaire } = useCampaignQuestionnaire(campaign?.id);

    const { reviews } = useCampaignReviews({
        state: [ReviewState.Approved, ReviewState.Submitted, ReviewState.Draft],
        campaignId: campaign?.id,
        limit: 50,
        productId: productId || campaign?.products?.[0]?.id,
    });

    const { approveCampaign, isApprovingCampaign } = useApproveCampaign(params?.campaignId);

    const previewLink = useMemo(() => {
        let campaignPath = `/campaigns/${campaign?.slug}`;

        if (campaign?.isHidden) {
            campaignPath = `/campaigns/${campaign?.id}`;
        }

        return `https://${campaign?.mainTenant?.domainName}/login?userType=client&redirect=${encodeURIComponent(campaignPath)}`;
    }, [campaign?.id, campaign?.isHidden, campaign?.mainTenant?.domainName, campaign?.slug]);

    return (
        <>
            <Flex
                mb="lg"
                pb="md"
                wrap="wrap"
                align="center"
                justify="space-between"
                rowGap={8}
                style={{
                    borderBottom: "1px solid #E0E0E0",
                }}
            >
                <Flex gap={16} align="center">
                    <IconChevronLeft
                        stroke={2}
                        size={24}
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate("/campaigns")}
                    />
                    <Title order={3} style={{ fontWeight: 600 }}>
                        {campaign?.name}
                    </Title>
                </Flex>
                <Flex gap={8} wrap="wrap">
                    {campaign?.state === CampaignState.Publishable && (
                        <Button
                            py={16}
                            pl={16}
                            pr={24}
                            radius={8}
                            variant="default"
                            leftIcon={<IconCheck size={18} />}
                            style={{ height: 48 }}
                            onClick={approveCampaign}
                        >
                            {isApprovingCampaign ? <Loader size="xs" /> : t("campaign_approve")}
                        </Button>
                    )}
                    {(campaign?.state === CampaignState.Draft ||
                        campaign?.state === CampaignState.Publishable ||
                        campaign?.state === CampaignState.Approved) && (
                        <Button
                            component="a"
                            target="_blank"
                            href={previewLink}
                            py={16}
                            pl={16}
                            pr={24}
                            radius={8}
                            leftIcon={<IconEyeCheck size={18} />}
                            style={{ height: 48 }}
                        >
                            {t("campaign_preview")}
                        </Button>
                    )}
                </Flex>
            </Flex>
            {isLoadingCampaign ? <Skeleton height={264} radius={12} /> : <OverviewWidget campaign={campaign} />}
            <Box mt={34} mb={24} w="100%">
                <TimelineWidget campaign={campaign} />
            </Box>
            {campaign?.products?.length > 1 && (
                <>
                    <h2>{t("campaign_products")}</h2>
                    <Flex>
                        {campaign?.products?.map((product, index) => {
                            const isActiveProduct = product?.id === (productId || campaign?.products?.[0]?.id);
                            return (
                                <Paper
                                    key={`${index}${product?.id}`}
                                    w={200}
                                    h={130}
                                    radius="lg"
                                    mr="md"
                                    sx={(theme) => ({
                                        overflow: "hidden",
                                        cursor: "pointer",
                                        border: `3px solid ${isActiveProduct ? theme.colors.orange[6] : "transparent"}`,
                                        boxShadow: isActiveProduct ? theme.shadows.md : "none",
                                        opacity: isActiveProduct ? 1 : 0.6,
                                        transition: "opacity 150ms ease, border-color 150ms ease",
                                        "&:hover": { opacity: 1 },
                                    })}
                                    onClick={() => setSearchParams({ ...searchParams, productId: product?.id })}
                                >
                                    {product?.thumbnailImage?.src ? (
                                        <Image src={product?.thumbnailImage?.src} fit="cover" w="100%" h="100%" />
                                    ) : (
                                        <Text>{product?.name}</Text>
                                    )}
                                </Paper>
                            );
                        })}
                    </Flex>
                </>
            )}
            <Tabs
                mt={34}
                color={colorScheme === "dark" ? "orange" : "dark"}
                keepMounted={false}
                value={tab}
                onTabChange={(tab) => navigate(`/campaign/${params?.campaignId}${tab === defaultTab ? "" : `/${tab}`}`)}
            >
                <Tabs.List mb={24}>
                    <Tabs.Tab value="assignment">{t("campaign_tab_assignment")}</Tabs.Tab>
                    <Tabs.Tab value="campaignapplications" disabled={isDraft} title={tabDisabledText}>
                        {t("campaign_tab_campaignapplications")}
                    </Tabs.Tab>
                    <Tabs.Tab value="reviews" disabled={isDraft} title={tabDisabledText}>
                        {t("campaign_tab_reviews")}
                    </Tabs.Tab>
                    <Tabs.Tab value="images" disabled={isDraft} title={tabDisabledText}>
                        {t("campaign_tab_media")}
                    </Tabs.Tab>
                    <Tabs.Tab value="report" disabled={isDraft || !isEvaluated} title={tabDisabledText}>
                        {reportTabLabel}
                    </Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="assignment">
                    <AssignmentTab campaign={campaign} questionnaire={questionnaire} />
                </Tabs.Panel>
                <Tabs.Panel value="campaignapplications">
                    <CampaignApplicationsTab campaign={campaign} questionnaire={questionnaire} />
                </Tabs.Panel>
                <Tabs.Panel value="reviews">
                    <ReviewsTab campaign={campaign} reviews={reviews} />
                </Tabs.Panel>
                <Tabs.Panel value="images">
                    <MediaTab campaign={campaign} reviews={reviews} />
                </Tabs.Panel>
                <Tabs.Panel value="report">
                    <ReportTab campaign={campaign} questionnaire={questionnaire} />
                </Tabs.Panel>
            </Tabs>
        </>
    );
};
